import React, { useEffect, useState } from "react";
import {
    ShieldCheck,
    RefreshCw,
    CheckCircle2,
    AlertTriangle,
    XCircle,
    Activity,
    Users as UsersIcon,
    FileSignature,
    Lock,
    UserX,
    ArrowRight,
} from "lucide-react";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useNavigate, Navigate } from "react-router-dom";
import { getSecurityOverview } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const STATUS_STYLE = {
    ok: {
        Icon: CheckCircle2,
        cls: "bg-emerald-50 text-emerald-700 border-emerald-200",
        dot: "bg-emerald-500",
        label: "OK",
    },
    warn: {
        Icon: AlertTriangle,
        cls: "bg-amber-50 text-amber-700 border-amber-200",
        dot: "bg-amber-500",
        label: "Aviso",
    },
    critical: {
        Icon: XCircle,
        cls: "bg-red-50 text-red-700 border-red-200",
        dot: "bg-red-500",
        label: "Crítico",
    },
};

const scoreColor = (score) => {
    if (score >= 90) return { text: "text-emerald-700", ring: "stroke-emerald-500", bg: "from-emerald-500 to-cyan-500" };
    if (score >= 70) return { text: "text-amber-700", ring: "stroke-amber-500", bg: "from-amber-500 to-orange-500" };
    return { text: "text-red-700", ring: "stroke-red-500", bg: "from-red-500 to-orange-500" };
};

const scoreLabel = (score) => {
    if (score >= 95) return "Excelente";
    if (score >= 85) return "Muy bueno";
    if (score >= 70) return "Aceptable";
    if (score >= 50) return "Revisión necesaria";
    return "Crítico";
};

const ScoreRing = ({ score }) => {
    const c = scoreColor(score);
    const radius = 80;
    const stroke = 12;
    const norm = radius - stroke / 2;
    const circ = 2 * Math.PI * norm;
    const offset = circ - (score / 100) * circ;
    return (
        <div className="relative w-[200px] h-[200px] shrink-0">
            <svg height={200} width={200} className="-rotate-90">
                <circle
                    cx={100}
                    cy={100}
                    r={norm}
                    stroke="#e2e8f0"
                    strokeWidth={stroke}
                    fill="transparent"
                />
                <circle
                    cx={100}
                    cy={100}
                    r={norm}
                    strokeWidth={stroke}
                    fill="transparent"
                    className={c.ring}
                    strokeLinecap="round"
                    strokeDasharray={`${circ} ${circ}`}
                    strokeDashoffset={offset}
                    style={{ transition: "stroke-dashoffset 0.8s ease-out" }}
                />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
                <div className={`font-display font-black text-5xl ${c.text}`}>{score}</div>
                <div className="text-[10px] font-bold uppercase tracking-widest text-ink-500 mt-1">
                    / 100
                </div>
            </div>
        </div>
    );
};

export default function Seguridad() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        try {
            const d = await getSecurityOverview();
            setData(d);
        } catch {
            toast.error("No se pudo cargar el dashboard de seguridad");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (user?.is_admin) load();
    }, [user]);

    if (!user?.is_admin) return <Navigate to="/app" replace />;

    const score = data?.score ?? 0;
    const counts = data?.counts || { ok: 0, warn: 0, critical: 0 };
    const indicators = data?.indicators || [];
    const metrics = data?.metrics || {};
    const colorMeta = scoreColor(score);

    return (
        <Layout>
            <div className="space-y-6" data-testid="security-page">
                {/* Header */}
                <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div>
                        <div className="overline mb-1.5">
                            <ShieldCheck size={12} className="inline mr-1" />
                            Seguridad · ISO 27001
                        </div>
                        <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tight text-ink-950">
                            Dashboard de seguridad
                        </h1>
                        <p className="text-ink-500 mt-2 text-base font-medium max-w-2xl">
                            Puntuación en vivo de la postura de ciberseguridad. Los auditores
                            ISO 27001 pueden re-calcular cada indicador desde la auditoría.
                        </p>
                    </div>
                    <Button
                        variant="outline"
                        onClick={load}
                        disabled={loading}
                        className="rounded-xl"
                        data-testid="security-refresh"
                    >
                        <RefreshCw
                            size={14}
                            className={`mr-1.5 ${loading ? "animate-spin" : ""}`}
                        />
                        Recalcular
                    </Button>
                </div>

                {/* Hero: score ring + counts + key metrics */}
                <div
                    className={`relative bg-gradient-to-br ${colorMeta.bg} p-1 rounded-3xl shadow-pop`}
                >
                    <div className="bg-white rounded-[22px] p-6 lg:p-8 flex flex-col lg:flex-row items-center gap-8">
                        <ScoreRing score={score} />
                        <div className="flex-1 min-w-0 w-full">
                            <div className="overline mb-1">Estado general</div>
                            <div
                                className={`font-display font-black text-3xl ${colorMeta.text} mb-3`}
                            >
                                {scoreLabel(score)}
                            </div>
                            <div className="grid grid-cols-3 gap-3 mb-4">
                                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3">
                                    <div className="flex items-center gap-1.5 text-xs font-bold text-emerald-700 mb-1">
                                        <CheckCircle2 size={12} /> OK
                                    </div>
                                    <div className="font-display font-black text-2xl text-emerald-700">
                                        {counts.ok}
                                    </div>
                                </div>
                                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
                                    <div className="flex items-center gap-1.5 text-xs font-bold text-amber-700 mb-1">
                                        <AlertTriangle size={12} /> Avisos
                                    </div>
                                    <div className="font-display font-black text-2xl text-amber-700">
                                        {counts.warn}
                                    </div>
                                </div>
                                <div className="bg-red-50 border border-red-200 rounded-xl p-3">
                                    <div className="flex items-center gap-1.5 text-xs font-bold text-red-700 mb-1">
                                        <XCircle size={12} /> Críticos
                                    </div>
                                    <div className="font-display font-black text-2xl text-red-700">
                                        {counts.critical}
                                    </div>
                                </div>
                            </div>
                            <div className="text-xs text-ink-400">
                                Última actualización:{" "}
                                {data?.generated_at
                                    ? new Date(data.generated_at).toLocaleString("es-ES")
                                    : "—"}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Key metrics row */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <MetricCard
                        icon={UsersIcon}
                        label="Usuarios"
                        value={metrics.total_users || 0}
                        sub={`${metrics.total_admins || 0} administradores`}
                        accent="brand"
                    />
                    <MetricCard
                        icon={UserX}
                        label="Cuentas inactivas (>90d)"
                        value={metrics.stale_users || 0}
                        sub={metrics.stale_users ? "Revisa y deshabilita" : "Todas activas"}
                        accent={metrics.stale_users ? "red" : "emerald"}
                    />
                    <MetricCard
                        icon={Lock}
                        label="Logins fallidos 7d"
                        value={metrics.failed_logins_7d || 0}
                        sub={
                            metrics.failed_logins_7d > 5
                                ? "Investigar"
                                : "Sin actividad sospechosa"
                        }
                        accent={metrics.failed_logins_7d > 20 ? "red" : metrics.failed_logins_7d > 5 ? "amber" : "emerald"}
                    />
                    <MetricCard
                        icon={FileSignature}
                        label="Anexos firmados (30d)"
                        value={metrics.signed_annexes_30d || 0}
                        sub="PAdES eIDAS"
                        accent="violet"
                    />
                </div>

                {/* Indicators list */}
                <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
                    <div className="px-5 py-3 border-b border-slate-200 bg-slate-50/60 flex items-center gap-2">
                        <Activity size={14} className="text-ink-500" />
                        <div className="font-display font-bold text-ink-950">
                            Indicadores ISO 27001 ({indicators.length})
                        </div>
                        <button
                            type="button"
                            onClick={() => navigate("/app/auditoria")}
                            className="ml-auto text-xs font-bold text-brand-600 hover:text-brand-800 flex items-center gap-1 rounded-xl px-2 py-1 hover:bg-brand-50"
                            data-testid="security-go-audit"
                        >
                            Ir a la auditoría
                            <ArrowRight size={12} />
                        </button>
                    </div>
                    <div className="divide-y divide-slate-100">
                        {indicators.map((it, i) => {
                            const meta = STATUS_STYLE[it.status] || STATUS_STYLE.ok;
                            const Icon = meta.Icon;
                            return (
                                <div
                                    key={`ind-${i}`}
                                    className="flex items-start gap-3 px-5 py-4 hover:bg-slate-50/50 transition-colors"
                                    data-testid={`security-indicator-${it.key}`}
                                >
                                    <div
                                        className={`w-9 h-9 rounded-xl border flex items-center justify-center shrink-0 ${meta.cls}`}
                                    >
                                        <Icon size={15} />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <div className="font-bold text-ink-950 text-sm">
                                                {it.label}
                                            </div>
                                            <span
                                                className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-md border ${meta.cls}`}
                                            >
                                                {meta.label}
                                            </span>
                                            {it.weight ? (
                                                <span className="text-[10px] font-bold text-ink-400 font-mono">
                                                    {it.weight} pts
                                                </span>
                                            ) : null}
                                        </div>
                                        <div className="text-xs text-ink-600 mt-0.5">
                                            {it.value}
                                        </div>
                                        {it.recommendation && (
                                            <div className="text-xs text-ink-500 mt-1 italic">
                                                💡 {it.recommendation}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Footnote */}
                <div className="bg-slate-50 border border-slate-200 rounded-2xl p-5 text-sm text-ink-600 leading-relaxed">
                    <strong className="text-ink-950">Sobre la puntuación:</strong> el score
                    arranca en 100 y se reduce según el peso negativo de cada indicador en
                    estado <em>aviso</em> o <em>crítico</em>. Es transparente y auditable —
                    cualquier auditor ISO 27001 puede re-calcularlo a partir de la
                    colección <code className="bg-white border border-slate-200 px-1 rounded">
                        audit_logs
                    </code>{" "}
                    y de la configuración actual. Consulta{" "}
                    <code className="bg-white border border-slate-200 px-1 rounded">
                        /app/SECURITY.md
                    </code>{" "}
                    para el mapeo completo de los 16 controles del Anexo A implementados.
                </div>
            </div>
        </Layout>
    );
}

const ACCENT = {
    brand: "border-brand-200 bg-brand-50 text-brand-700",
    red: "border-red-200 bg-red-50 text-red-700",
    amber: "border-amber-200 bg-amber-50 text-amber-700",
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-700",
    violet: "border-violet-200 bg-violet-50 text-violet-700",
};

function MetricCard({ icon: Icon, label, value, sub, accent }) {
    const cls = ACCENT[accent] || ACCENT.brand;
    return (
        <div className={`border rounded-2xl p-5 ${cls}`}>
            <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider mb-2 opacity-80">
                <Icon size={12} />
                {label}
            </div>
            <div className="font-display font-black text-3xl">
                {value.toLocaleString("es-ES")}
            </div>
            <div className="text-[11px] font-medium mt-1 opacity-70">{sub}</div>
        </div>
    );
}
